import React, { useEffect, useState } from "react";
import {
    Card, Row, Col, Select, DatePicker, Input, Button, Radio,
    Typography, Checkbox, message, Tooltip, Popconfirm,
} from "antd";
import {
    AimOutlined, UserOutlined, PhoneOutlined, FileTextOutlined,
    CheckCircleOutlined, DollarOutlined, ShoppingCartOutlined,
    TeamOutlined, MailOutlined, UsergroupAddOutlined, IdcardOutlined,
    BellOutlined, InfoCircleOutlined, ArrowLeftOutlined, PlusOutlined,
    EditOutlined, DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";

import { useNavigate, useSearchParams } from "react-router-dom";
import { AddEmployeesService } from "../../../Services/Manage/AddEmployeesService";
import { ConfigurationService } from "../../../Services/Manage/ConfigurationService";
import { UserService } from "../../../Services/Manage/UserService";

import { SalesTargetService } from "../../../Services/Manage/SalesTargetService";
import { SalesTeamService } from "../../../Services/Manage/SalesTeamService";
import { NotificationService } from "../../../Services/Manage/NotificationService";
import { EmailConfigService } from "../../../Services/Manage/EmailConfigService";
dayjs.extend(quarterOfYear);
const PRIMARY = "rgba(11, 93, 148, 0.95)";

const compactStyle = `
  .st-form .ant-card-body { padding: 16px !important; }
  .st-form .ant-card { margin-bottom: 16px !important; }
`;

const CardTitle = ({ no, icon, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: PRIMARY }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no ? `${no}. ` : ""}{text}</span>
    </span>
);

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch { return { add: false, edit: false, delete: false, read: false }; }
};

const periodOptions = [
    { value: "Monthly", label: "Monthly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Yearly", label: "Yearly" },
];
const priorityOptions = [
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "High", label: "High" },
    { value: "Urgent", label: "Urgent" },
];
const reminderOptions = [
    { value: "1 Day Before", label: "1 Day Before" },
    { value: "3 Days Before", label: "3 Days Before" },
    { value: "1 Week Before", label: "1 Week Before" },
];
const notifyOptions = [
    { value: "Sales Executive", label: "Sales Executive" },
    { value: "Reporting Manager", label: "Reporting Manager" },
    { value: "Both", label: "Both" },
];

const metrics = [
    { key: "lead_target", label: "Lead Target", icon: <UserOutlined />, color: "#52c41a" },
    { key: "followup_target", label: "Follow-up Target", icon: <PhoneOutlined />, color: "#1677ff" },
    { key: "quotation_target", label: "Quotation Target", icon: <FileTextOutlined />, color: "#722ed1" },
    { key: "conversion_target", label: "Conversion Target (Won Deals)", icon: <CheckCircleOutlined />, color: "#13c2c2" },
    { key: "revenue_target", label: "Revenue Target (₹)", icon: <DollarOutlined />, color: "#fa8c16", money: true },
    { key: "collection_target", label: "Collection Target (₹)", icon: <DollarOutlined />, color: "#13c2c2", money: true },
    { key: "sales_order_target", label: "Sales Order Target", icon: <ShoppingCartOutlined />, color: "#eb2f96" },
    { key: "demo_meeting_target", label: "Demo / Meeting Target", icon: <TeamOutlined />, color: "#1677ff" },
    { key: "email_target", label: "Email Target", icon: <MailOutlined />, color: "#722ed1" },
    { key: "call_target", label: "Call Target", icon: <PhoneOutlined />, color: "#fa8c16" },
    { key: "new_customers_target", label: "New Customers Target", icon: <UsergroupAddOutlined />, color: "#52c41a" },
    { key: "existing_customers_target", label: "Existing Customers Target", icon: <IdcardOutlined />, color: "#1677ff" },
];

const AddSalesTarget = () => {
    const perm = getPermission("/add-sales-target");
    const navigate = useNavigate();
    const [msg, msgCtx] = message.useMessage();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get("id");
    const mode = searchParams.get("mode") || (editId ? "edit" : "create");
    const isView = mode === "view";

    const [executives, setExecutives] = useState([]);
    const [territories, setTerritories] = useState([]);
    const [targetTypeOptions, setTargetTypeOptions] = useState([]);
    const [managers, setManagers] = useState([]);
    const [saving, setSaving] = useState(false);

    const [existingTeams, setExistingTeams] = useState([]); // [{ id, name, memberIds: [] }]
    const [isCreatingTeam, setIsCreatingTeam] = useState(false);
    const [editingTeamId, setEditingTeamId] = useState(null); // null = creating, id = editing
    const [newTeamName, setNewTeamName] = useState("");
    const [newTeamMembers, setNewTeamMembers] = useState([]);
    const [teamSaving, setTeamSaving] = useState(false);


    const [newTeamLeaderId, setNewTeamLeaderId] = useState(null);
    const [memberSplits, setMemberSplits] = useState({});   // { metricKey: { memberId: value } }
    const [lockedCells, setLockedCells] = useState({});       // { metricKey: Set(memberId) }

    const blankMetrics = metrics.reduce((a, m) => ({ ...a, [m.key]: 0 }), {});

    const [form, setForm] = useState({
        target_for: "Sales Executive",
        sales_executive_id: null,
        sales_executive_name: null,
        team_member_ids: null,
        team_name: null,
        designation: null,
        reporting_manager: null,
        territory: null,
        target_type: null,
        period: "Monthly",
        target_month: dayjs(),
        start_date: dayjs().startOf("month"),
        end_date: dayjs().endOf("month"),
        ...blankMetrics,
        priority: "Medium",
        remarks: "",
        notify_to: "Sales Executive",
        reminder: "1 Day Before",
        send_email: true,
    });


    const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const distribute = (total, memberIds, current = {}, locked = new Set()) => {
        const t = Number(total) || 0;
        const out = {};
        const lockedSum = memberIds
            .filter((id) => locked.has(String(id)))
            .reduce((s, id) => s + (Number(current[id]) || 0), 0);
        memberIds.forEach((id) => {
            if (locked.has(String(id))) out[id] = Number(current[id]) || 0;
        });
        const unlocked = memberIds.filter((id) => !locked.has(String(id)));
        let remaining = Math.max(0, t - lockedSum);
        if (unlocked.length > 0) {
            const base = Math.floor(remaining / unlocked.length);
            let extra = remaining - base * unlocked.length;
            unlocked.forEach((id) => {
                out[id] = base + (extra > 0 ? 1 : 0);
                if (extra > 0) extra -= 1;
            });
        }
        return out;
    };

    const teamMemberIds = () =>
        (form.team_member_ids || "").split(",").map((s) => s.trim()).filter(Boolean);

    const redistributeMetric = (metricKey, total) => {
        const ids = teamMemberIds();
        if (ids.length === 0) return;
        setMemberSplits((prev) => {
            const cur = prev[metricKey] || {};
            const locked = lockedCells[metricKey] || new Set();
            return { ...prev, [metricKey]: distribute(total, ids, cur, locked) };
        });
    };

    // Max a given member cell may hold = team total minus all OTHER locked cells
    const cellMax = (metricKey, memberId) => {
        const ids = teamMemberIds();
        const total = Number(form[metricKey]) || 0;
        const cur = memberSplits[metricKey] || {};
        const lockSet = lockedCells[metricKey] || new Set();
        const otherLockedSum = ids
            .filter((id) => String(id) !== String(memberId) && lockSet.has(String(id)))
            .reduce((s, id) => s + (Number(cur[id]) || 0), 0);
        return Math.max(0, total - otherLockedSum);
    };

    // While typing: store raw value, lock the cell, rebalance the rest. No clamping yet.
    const handleMemberCellChange = (metricKey, memberId, value) => {
        const ids = teamMemberIds();
        const total = Number(form[metricKey]) || 0;
        setLockedCells((prevLocks) => {
            const lockSet = new Set(prevLocks[metricKey] || []);
            lockSet.add(String(memberId));
            setMemberSplits((prevSplits) => {
                const cur = { ...(prevSplits[metricKey] || {}) };
                cur[memberId] = value === "" ? "" : Number(value) || 0;
                const redistributed = distribute(total, ids, cur, lockSet);
                redistributed[memberId] = cur[memberId];
                return { ...prevSplits, [metricKey]: redistributed };
            });
            return { ...prevLocks, [metricKey]: lockSet };
        });
    };

    // On blur: clamp to the allowed max and rebalance cleanly.
    const handleMemberCellBlur = (metricKey, memberId) => {
        const ids = teamMemberIds();
        const total = Number(form[metricKey]) || 0;
        const max = cellMax(metricKey, memberId);
        setMemberSplits((prevSplits) => {
            const cur = { ...(prevSplits[metricKey] || {}) };
            let v = Number(cur[memberId]) || 0;
            if (v > max) v = max;
            if (v < 0) v = 0;
            cur[memberId] = v;
            const lockSet = lockedCells[metricKey] || new Set();
            const redistributed = distribute(total, ids, cur, lockSet);
            redistributed[memberId] = v;
            return { ...prevSplits, [metricKey]: redistributed };
        });
    };

    const loadExecutives = async () => {
        try {
            const res = await AddEmployeesService.getByDepartment("Sales");
            console.log("executives res", res);
            setExecutives(res.data || []);
        } catch (e) { console.error(e); }
    };

    const loadConfigs = async () => {
        try {
            const terr = await ConfigurationService.getByType("territory");
            setTerritories((terr.data || []).map((i) => ({ value: i.label, label: i.label })));
        } catch (e) { console.error(e); }
        try {
            const tt = await ConfigurationService.getByType("target_type");
            setTargetTypeOptions((tt.data || []).map((i) => ({ value: i.label, label: i.label })));
        } catch (e) { console.error(e); }
    };

    const loadManagers = async () => {
        try {
            const res = await UserService.getAllUsers();
            setManagers(
                (res.data || [])
                    .filter((u) => u.status === "Active")
                    .map((u) => ({ value: u.name, label: u.name }))
            );
        } catch (e) { console.error(e); }
    };

    const loadExistingTeams = async () => {
        try {
            const res = await SalesTeamService.getAll();
            const rows = res.data || [];
            setExistingTeams(
                rows.map((t) => ({
                    id: t.id,
                    name: t.name,
                    memberIds: Array.isArray(t.member_ids)
                        ? t.member_ids.map(String)
                        : (t.member_ids ? JSON.parse(t.member_ids).map(String) : []),
                    leaderId: t.team_leader_id ? String(t.team_leader_id) : null,
                }))
            );
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        loadExecutives();
        loadConfigs();
        loadManagers();
        loadExistingTeams();
    }, []);

    // When editing/viewing, fetch the target and populate the form.
    // Runs after executives load so member-split names resolve.
    useEffect(() => {
        if (!editId) return;
        if (executives.length === 0) return; // wait for execs (needed for splits)
        (async () => {
            try {
                const res = await SalesTargetService.getById(editId);
                if (!res.status || !res.data) {
                    msg.error(res.message || "Target not found");
                    return;
                }
                const t = res.data;

                // Rebuild member splits from saved member_targets jsonb
                const mt = Array.isArray(t.member_targets)
                    ? t.member_targets
                    : (t.member_targets ? JSON.parse(t.member_targets) : []);
                const splits = {};
                metrics.forEach((m) => { splits[m.key] = {}; });
                mt.forEach((row) => {
                    metrics.forEach((m) => {
                        splits[m.key][String(row.member_id)] = Number(row[m.key]) || 0;
                    });
                });
                setMemberSplits(splits);
                setLockedCells({}); // editing starts unlocked; values are pre-filled

                setForm((p) => ({
                    ...p,
                    target_for: t.target_for || "Sales Executive",
                    sales_executive_id: t.sales_executive_id ? String(t.sales_executive_id) : null,
                    sales_executive_name: t.sales_executive_name || null,
                    team_member_ids: t.team_member_ids || null,
                    team_name: t.team_name || null,
                    designation: t.designation || null,
                    reporting_manager: t.reporting_manager || null,
                    territory: t.territory || null,
                    target_type: t.target_type || null,
                    period: t.period || "Monthly",
                    target_month: t.start_date ? dayjs(t.start_date) : dayjs(),
                    start_date: t.start_date ? dayjs(t.start_date) : dayjs().startOf("month"),
                    end_date: t.end_date ? dayjs(t.end_date) : dayjs().endOf("month"),
                    ...metrics.reduce((a, m) => ({ ...a, [m.key]: Number(t[m.key]) || 0 }), {}),
                    priority: t.priority || "Medium",
                    remarks: t.remarks || "",
                    notify_to: t.notify_to || "Sales Executive",
                    reminder: t.reminder || "1 Day Before",
                    send_email: t.send_email ?? true,
                }));
            } catch (e) {
                console.error(e);
                msg.error("Failed to load target");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId, executives]);

    const execOptions = executives.map((e) => ({
        value: String(e.id),
        label: `${e.name}${e.uid ? ` (${e.uid})` : ""}`,
    }));

    const handleExecChange = (v) => {
        if (!v) {
            setForm((p) => ({
                ...p,
                sales_executive_id: null,
                sales_executive_name: null,
                designation: null,
                reporting_manager: null,
                territory: null,
            }));
            return;
        }
        const ex = executives.find((e) => String(e.id) === String(v));
        setForm((p) => ({
            ...p,
            sales_executive_id: v,
            sales_executive_name: ex?.name || null,
            designation: ex?.designation || ex?.job_title || null,
            reporting_manager: ex?.reporting_manager || null,
            territory: ex?.territory || null,
        }));
    };

    const handleTargetForChange = (e) => {
        const v = e.target.value;
        setIsCreatingTeam(false);
        setNewTeamName("");
        setNewTeamMembers([]);
        setForm((p) => ({
            ...p,
            target_for: v,
            sales_executive_id: null,
            sales_executive_name: null,
            team_member_ids: null,
            team_name: null,
            designation: null,
            reporting_manager: null,
            territory: null,
        }));
    };

    const handleTeamSelectChange = (v) => {
        if (v === "__create_new__") {
            setIsCreatingTeam(true);
            setNewTeamName("");
            setNewTeamMembers([]);
            setForm((p) => ({ ...p, sales_executive_id: null, team_name: null, team_leader_id: null }));
            return;
        }
        setIsCreatingTeam(false);
        if (!v) {
            setForm((p) => ({ ...p, sales_executive_id: null, team_name: null, team_leader_id: null }));
            return;
        }
        const team = existingTeams.find((t) => t.name === v);
        const ids = team ? team.memberIds : [];
        setEditingTeamId(null);
        setForm((p) => {
            const next = { ...p, sales_executive_id: null, team_member_ids: ids.join(","), team_name: v, team_leader_id: team?.leaderId || null };
            const splits = {};
            metrics.forEach((m) => { splits[m.key] = distribute(Number(next[m.key]) || 0, ids); });
            setMemberSplits(splits);
            setLockedCells({});
            return next;
        });
    };

    const handleEditTeam = (team) => {
        setIsCreatingTeam(true);
        setEditingTeamId(team.id);
        setNewTeamName(team.name);
        setNewTeamMembers(team.memberIds);
        setNewTeamLeaderId(team.leaderId || null);
        setForm((p) => ({ ...p, team_name: null, team_member_ids: null }));
    };

    // Delete a team from the table
    const handleDeleteTeam = async (team) => {
        try {
            const res = await SalesTeamService.remove(team.id);
            if (res.status) {
                msg.success("Team deleted");
                if (form.team_name === team.name) {
                    setForm((p) => ({ ...p, team_name: null, team_member_ids: null }));
                }
                loadExistingTeams();
            } else {
                msg.error(res.message || "Failed to delete team");
            }
        } catch (e) {
            console.error(e);
            msg.error("Failed to delete team");
        }
    };

    const handleSaveNewTeam = async () => {
        if (!newTeamName.trim()) return msg.warning("Enter a team name");
        if (newTeamMembers.length === 0) return msg.warning("Select at least one sales executive");
        const nameExists = existingTeams.some(
            (t) => t.name.toLowerCase() === newTeamName.trim().toLowerCase() && t.id !== editingTeamId
        );
        if (nameExists) return msg.warning("A team with this name already exists");

        setTeamSaving(true);
        try {
            const user = await UserService.getOmeUser();
            const body = {
                name: newTeamName.trim(),
                member_ids: newTeamMembers.map(String),
                team_leader_id: newTeamLeaderId || null,
                added_by: user?.uid || null,
            };
            const res = editingTeamId
                ? await SalesTeamService.update(editingTeamId, body)
                : await SalesTeamService.create(body);

            if (!res.status) {
                msg.error(res.message || "Failed to save team");
                return;
            }

            const saved = res.data;
            const savedIds = (Array.isArray(saved.member_ids)
                ? saved.member_ids
                : JSON.parse(saved.member_ids || "[]")).map(String);

            await loadExistingTeams();

            setForm((p) => {
                const next = { ...p, sales_executive_id: null, team_member_ids: savedIds.join(","), team_name: saved.name, team_leader_id: saved.team_leader_id || null };
                const splits = {};
                metrics.forEach((m) => { splits[m.key] = distribute(Number(next[m.key]) || 0, savedIds); });
                setMemberSplits(splits);
                setLockedCells({});
                return next;
            });

            setIsCreatingTeam(false);
            setEditingTeamId(null);
            setNewTeamName("");
            setNewTeamMembers([]);
            msg.success(editingTeamId ? "Team updated" : "Team created");
        } catch (e) {
            console.error(e);
            msg.error("Failed to save team");
        } finally {
            setTeamSaving(false);
        }
    };

    const handlePeriodChange = (v) => {
        setForm((p) => {
            const base = p.target_month || dayjs();
            let start = base.startOf("month");
            let end = base.endOf("month");
            if (v === "Quarterly") { start = base.startOf("quarter"); end = base.endOf("quarter"); }
            if (v === "Yearly") { start = base.startOf("year"); end = base.endOf("year"); }
            return { ...p, period: v, start_date: start, end_date: end };
        });
    };

    const handleMonthChange = (v) => {
        if (!v) return setField("target_month", null);
        setForm((p) => {
            let start = v.startOf("month");
            let end = v.endOf("month");
            if (p.period === "Quarterly") { start = v.startOf("quarter"); end = v.endOf("quarter"); }
            if (p.period === "Yearly") { start = v.startOf("year"); end = v.endOf("year"); }
            return { ...p, target_month: v, start_date: start, end_date: end };
        });
    };

    const handleReset = () => {
        setIsCreatingTeam(false);
        setEditingTeamId(null);
        setNewTeamName("");
        setNewTeamMembers([]);
        setMemberSplits({});
        setLockedCells({});
        setForm({
            target_for: "Sales Executive",
            sales_executive_id: null, sales_executive_name: null, team_member_ids: null, team_name: null,
            designation: null, reporting_manager: null, territory: null,
            target_type: null, period: "Monthly",
            target_month: dayjs(),
            start_date: dayjs().startOf("month"),
            end_date: dayjs().endOf("month"),
            ...blankMetrics,
            priority: "Medium", remarks: "",
            notify_to: "Sales Executive", reminder: "1 Day Before", send_email: true,
        });
    };

    const handleSave = async () => {
        if (editId ? !perm.edit : !perm.add)
            return msg.warning(editId ? "You don't have permission to edit" : "You don't have permission to create");
        if (form.target_for === "Sales Team") {
            if (!form.team_name) return msg.warning("Please select or create a sales team");
        } else {
            if (!form.sales_executive_id) return msg.warning("Please select a sales executive");
        }
        if (!form.target_type) return msg.warning("Please select a target type");
        if (form.target_for === "Sales Executive" && !form.territory) {
            return msg.warning("Please select a territory");
        }

        setSaving(true);
        try {
            const user = await UserService.getOmeUser();
            let member_targets = [];
            if (form.target_for === "Sales Team") {
                member_targets = teamMemberIds().map((id) => {
                    const exec = executives.find((e) => String(e.id) === String(id));
                    const row = { member_id: id, member_name: exec?.name || null };
                    metrics.forEach((m) => { row[m.key] = Number((memberSplits[m.key] || {})[id]) || 0; });
                    return row;
                });
            }

            const targetMonthStr = form.target_month
                ? (form.period === "Yearly" ? form.target_month.format("YYYY")
                    : form.period === "Quarterly" ? `Q${form.target_month.quarter()} ${form.target_month.format("YYYY")}`
                        : form.target_month.format("MMMM YYYY"))
                : null;

            const payload = {
                ...form,
                target_month: targetMonthStr,
                start_date: form.start_date ? form.start_date.format("YYYY-MM-DD") : null,
                end_date: form.end_date ? form.end_date.format("YYYY-MM-DD") : null,
                member_targets,
                added_by: user?.uid || null,
            };
            const res = editId
                ? await SalesTargetService.update(editId, payload)
                : await SalesTargetService.create(payload);
            if (res.status) {
                msg.success(res.message || (editId ? "Target updated successfully" : "Target saved successfully"));

                // Send in-app notification (saved to DB, fetched per-user)
                try {
                    const targetLabel = form.target_for === "Sales Team"
                        ? `Sales Team: ${form.team_name}`
                        : `${form.sales_executive_name || ""}`;

                    let recipients = [];

                    if (form.notify_to === "Sales Executive" || form.notify_to === "Both") {
                        if (form.target_for === "Sales Team") {
                            teamMemberIds().forEach((id) => {
                                const exec = executives.find((e) => String(e.id) === String(id));
                                if (exec) recipients.push({ uid: exec.uid || null, name: exec.name || null });
                            });
                        } else if (form.sales_executive_id) {
                            const exec = executives.find((e) => String(e.id) === String(form.sales_executive_id));
                            if (exec) recipients.push({ uid: exec.uid || null, name: exec.name || null });
                        }
                    }

                    if (form.notify_to === "Reporting Manager" || form.notify_to === "Both") {
                        if (form.reporting_manager) {
                            const mgr = executives.find((e) => e.name === form.reporting_manager);
                            recipients.push({ uid: mgr?.uid || null, name: form.reporting_manager });
                        }
                    }

                    const savedTargetId = res.data?.id || editId || null;

                    if (editId && savedTargetId) {
                        try { await NotificationService.removeByTarget(savedTargetId); } catch { }
                    }

                    if (recipients.length > 0) {
                        await NotificationService.create({
                            type: "sales_target",
                            title: editId ? "Sales Target Updated" : "New Sales Target Assigned",
                            message: `${targetLabel} — ${form.period} target for ${targetMonthStr || ""} has been ${editId ? "updated" : "assigned"}.`,
                            reminder: form.reminder || null,
                            end_date: form.end_date ? form.end_date.format("YYYY-MM-DD") : null,
                            link: form.target_for === "Sales Team"
                                ? `/sales-target?id=${savedTargetId}`
                                : `/enquiry/sales-performance/${encodeURIComponent(form.sales_executive_name || "")}`,
                            meta: {
                                period: form.period,
                                targetMonth: targetMonthStr || "",
                                targetId: savedTargetId,
                            },
                            added_by: user?.uid || null,
                            recipients,
                        });
                    }
                } catch (e) { console.error("notification error:", e); }

                // Send reminder email only if today is on or after the reminder date
                const endDateVal = form.end_date ? form.end_date : null;
                const reminderDaysMap = { "1 Day Before": 1, "3 Days Before": 3, "1 Week Before": 7 };
                const daysBeforeVal = reminderDaysMap[form.reminder] || 1;
                const reminderDateVal = endDateVal ? endDateVal.clone().subtract(daysBeforeVal, "day") : null;
                const todayVal = dayjs().startOf("day");
                const shouldSendEmail = form.send_email && reminderDateVal && todayVal.isSameOrAfter(reminderDateVal);
                if (shouldSendEmail) {
                    try {
                        const toEmails = [];
                        if (form.notify_to === "Sales Executive" || form.notify_to === "Both") {
                            if (form.target_for === "Sales Team") {
                                teamMemberIds().forEach((id) => {
                                    const exec = executives.find((e) => String(e.id) === String(id));
                                    if (exec?.email) toEmails.push(exec.email);
                                });
                            } else {
                                const exec = executives.find((e) => String(e.id) === String(form.sales_executive_id));
                                if (exec?.email) toEmails.push(exec.email);
                            }
                        }
                        if (form.notify_to === "Reporting Manager" || form.notify_to === "Both") {
                            const mgr = executives.find((e) => e.name === form.reporting_manager);
                            if (mgr?.email) toEmails.push(mgr.email);
                        }

                        if (toEmails.length > 0) {
                            const targetLabel = form.target_for === "Sales Team"
                                ? `Sales Team: ${form.team_name}`
                                : form.sales_executive_name || "";
                            await EmailConfigService.sendReminder({
                                to: toEmails,
                                subject: `Sales Target Reminder — ${form.reminder} before deadline`,
                                html: `
                                    <p>Hi,</p>
                                    <p>This is a reminder about your Sales Target.</p>
                                    <p><strong>Target For:</strong> ${targetLabel}</p>
                          <p><strong>Period:</strong> ${form.period} — ${targetMonthStr || ""}</p>
                                    <p><strong>Reminder:</strong> ${form.reminder}</p>
                                    <p><strong>End Date:</strong> ${form.end_date ? form.end_date.format("DD-MMM-YYYY") : "N/A"}</p>
                                    <p>Please log in to OMS to review your targets.</p>
                                `,
                            });
                            msg.success("Reminder email sent");
                        }
                    } catch (e) {
                        console.error("email error:", e);
                        msg.warning("Target saved but reminder email failed");
                    }
                }

                if (editId) {
                    navigate("/sales-target");
                } else {
                    handleReset();
                    loadExistingTeams();
                }
            } else {
                msg.error(res.message || "Failed to save target");
            }
        } catch (e) {
            console.error(e);
            msg.error("Failed to save target");
        } finally {
            setSaving(false);
        }
    };

    const labelStyle = { fontSize: 13, color: "#555", marginBottom: 4, display: "block" };
    const reqMark = <span style={{ color: "red" }}> *</span>;

    if (!perm.read) {
        return (
            <Card style={{ margin: 16 }}>
                <Typography.Text type="danger">
                    You don't have permission to view this page.
                </Typography.Text>
            </Card>
        );
    }

    return (
        <>
            {msgCtx}
            <style>{compactStyle}</style>
            <Card
                className="st-form"
                style={{ marginBottom: 16 }}
                title={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontWeight: 600, fontSize: 20 }}>
                            {isView ? "View Target" : editId ? "Edit Target" : "Add Target"}
                        </span>
                    </span>
                }
                extra={<Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>Back to Targets</Button>}
            >
                <div className="st-form">
                    {/* 1. Select Target For */}
                    <Card title={<CardTitle no="1" icon={<FileTextOutlined />} text="Select Target For" />}>
                        {form.target_for === "Sales Executive" ? (
                            <Row gutter={16} align="bottom">
                                <Col xs={24} md={5}>
                                    <span style={labelStyle}>Target For{reqMark}</span>
                                    <div style={{ height: 40, display: "flex", alignItems: "center" }}>
                                        <Radio.Group value={form.target_for} onChange={handleTargetForChange}>
                                            <Radio value="Sales Executive">Sales Executive</Radio>
                                            <Radio value="Sales Team">Sales Team</Radio>
                                        </Radio.Group>
                                    </div>
                                </Col>
                                <Col xs={24} md={5}>
                                    <span style={labelStyle}>Sales Executive{reqMark}</span>
                                    <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                        placeholder="Select sales executive" options={execOptions}
                                        value={form.sales_executive_id} onChange={handleExecChange}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={4}>
                                    <span style={labelStyle}>Designation</span>
                                    <Input size="large" readOnly placeholder="-" value={form.designation || ""} />
                                </Col>
                                <Col xs={24} md={5}>
                                    <span style={labelStyle}>Reporting Manager</span>
                                    <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                        placeholder="Select reporting manager"
                                        options={
                                            form.reporting_manager && !executives.some((e) => e.name === form.reporting_manager)
                                                ? [{ value: form.reporting_manager, label: form.reporting_manager }]
                                                : executives.map((e) => ({ value: e.name, label: e.name }))
                                        }
                                        value={form.reporting_manager} onChange={(v) => setField("reporting_manager", v)}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                                <Col xs={24} md={5}>
                                    <span style={labelStyle}>Territory{reqMark}</span>
                                    <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                        placeholder="Select territory" options={territories}
                                        value={form.territory} onChange={(v) => setField("territory", v)}
                                        filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                </Col>
                            </Row>
                        ) : (
                            <>
                                <Row gutter={16} align="bottom">
                                    <Col xs={24} md={4}>
                                        <span style={labelStyle}>Target For{reqMark}</span>
                                        <div style={{ height: 40, display: "flex", alignItems: "center" }}>
                                            <Radio.Group value={form.target_for} onChange={handleTargetForChange}>
                                                <Radio value="Sales Executive">Sales Executive</Radio>
                                                <Radio value="Sales Team">Sales Team</Radio>
                                            </Radio.Group>
                                        </div>
                                    </Col>

                                    <Col xs={24} md={5}>
                                        <span style={labelStyle}>Sales Team{reqMark}</span>
                                        <Select
                                            showSearch
                                            allowClear
                                            size="large"
                                            style={{ width: "100%" }}
                                            placeholder="Select sales team"
                                            value={isCreatingTeam ? "__create_new__" : (form.team_name || undefined)}
                                            onChange={handleTeamSelectChange}
                                            filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                                            options={[
                                                ...existingTeams.map((t) => ({ value: t.name, label: t.name })),
                                                { value: "__create_new__", label: "+ Create New Team" },
                                            ]}
                                        />
                                    </Col>

                                    {isCreatingTeam && (
                                        <>
                                            <Col xs={24} md={4}>
                                                <span style={labelStyle}>Team Name{reqMark}</span>
                                                <Input
                                                    size="large"
                                                    placeholder="Team Name"
                                                    value={newTeamName}
                                                    onChange={(e) => setNewTeamName(e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={24} md={9}>
                                                <span style={labelStyle}>Sales Executives{reqMark} <span style={{ color: "#999", fontWeight: 400 }}>(click ★ to set leader)</span></span>
                                                <Select
                                                    mode="multiple"
                                                    maxTagCount="responsive"
                                                    showSearch
                                                    allowClear
                                                    size="large"
                                                    style={{ width: "100%" }}
                                                    placeholder="Select sales executives"
                                                    options={execOptions}
                                                    value={newTeamMembers}
                                                    onChange={(ids) => {
                                                        setNewTeamMembers(ids);
                                                        if (newTeamLeaderId && !ids.includes(newTeamLeaderId)) setNewTeamLeaderId(null);
                                                    }}
                                                    filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())}
                                                    tagRender={(props) => {
                                                        const { value, label, closable, onClose } = props;
                                                        const isLeader = String(value) === String(newTeamLeaderId);
                                                        return (
                                                            <span style={{
                                                                display: "inline-flex", alignItems: "center", gap: 4,
                                                                border: "1px solid #d9d9d9", borderRadius: 4, padding: "0 6px",
                                                                margin: "2px 4px 2px 0", fontSize: 12,
                                                                background: isLeader ? "#fff7e6" : "#fafafa",
                                                            }}>
                                                                <span
                                                                    onMouseDown={(e) => e.preventDefault()}
                                                                    onClick={() => setNewTeamLeaderId(isLeader ? null : value)}
                                                                    style={{ cursor: "pointer", color: isLeader ? "#fa8c16" : "#bbb" }}
                                                                    title="Set as team leader"
                                                                >★</span>
                                                                <span>{label}</span>
                                                                {closable && (
                                                                    <span onClick={onClose} style={{ cursor: "pointer", color: "#999" }}>×</span>
                                                                )}
                                                            </span>
                                                        );
                                                    }}
                                                />
                                            </Col>
                                            <Col xs={24} md={2}>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    size="large"
                                                    loading={teamSaving}
                                                    style={{ background: PRIMARY, borderColor: PRIMARY, width: "100%" }}
                                                    onClick={handleSaveNewTeam}
                                                >
                                                    {editingTeamId ? "Save" : "Add"}
                                                </Button>
                                            </Col>
                                        </>
                                    )}
                                    {!isCreatingTeam && form.team_name && (() => {
                                        const selected = existingTeams.find((t) => t.name === form.team_name);
                                        return (
                                            <Col xs={24} md={10}>
                                                <span style={labelStyle}>Members</span>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40 }}>
                                                    <div style={{
                                                        flex: 1, display: "flex", alignItems: "center",
                                                        fontSize: 13, color: "#555", overflow: "hidden",
                                                        textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    }}>
                                                        <TeamOutlined style={{ marginRight: 6, color: PRIMARY, flexShrink: 0 }} />
                                                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 4, flexWrap: "nowrap" }}>
                                                            {(form.team_member_ids || "")
                                                                .split(",")
                                                                .filter(Boolean)
                                                                .map((id) => {
                                                                    const nm = executives.find((e) => String(e.id) === String(id))?.name;
                                                                    if (!nm) return null;
                                                                    const isLeader = selected?.leaderId && String(selected.leaderId) === String(id);
                                                                    return (
                                                                        <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 2, whiteSpace: "nowrap" }}>
                                                                            {nm}
                                                                            {isLeader && (
                                                                                <Tooltip title="Team Leader">
                                                                                    <span style={{ color: "#fa8c16" }}>★</span>
                                                                                </Tooltip>
                                                                            )}
                                                                        </span>
                                                                    );
                                                                })
                                                                .reduce((acc, el, i) => (el ? [...acc, i > 0 && acc.length ? ", " : "", el] : acc), [])}
                                                            {!(form.team_member_ids || "").split(",").filter(Boolean).length && "—"}
                                                        </span>
                                                    </div>
                                                    {selected && (
                                                        <>
                                                            <Tooltip title="Edit team">
                                                                <Button size="small" icon={<EditOutlined />}
                                                                    onClick={() => handleEditTeam(selected)} />
                                                            </Tooltip>
                                                            <Popconfirm
                                                                title="Delete this team?"
                                                                onConfirm={() => handleDeleteTeam(selected)}
                                                                okText="Delete" cancelText="Cancel"
                                                            >
                                                                <Tooltip title="Delete team">
                                                                    <Button size="small" danger icon={<DeleteOutlined />} />
                                                                </Tooltip>
                                                            </Popconfirm>
                                                        </>
                                                    )}
                                                </div>
                                            </Col>
                                        );
                                    })()}
                                </Row>
                            </>
                        )}
                    </Card>

                    {/* 2. Target Details */}
                    <Card title={<CardTitle no="2" icon={<AimOutlined />} text="Target Details" />}>
                        <Row gutter={16}>
                            <Col xs={24} md={5}>
                                <span style={labelStyle}>Target Type (Configuration) {reqMark}</span>
                                <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                    placeholder="Select target type" options={targetTypeOptions}
                                    value={form.target_type} onChange={(v) => setField("target_type", v)}
                                    filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                            </Col>
                            <Col xs={24} md={5}>
                                <span style={labelStyle}>Period{reqMark}</span>
                                <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                    options={periodOptions} value={form.period} onChange={handlePeriodChange}
                                    filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                            </Col>
                            <Col xs={24} md={5}>
                                <span style={labelStyle}>{form.period === "Yearly" ? "Year" : form.period === "Quarterly" ? "Quarter" : "Month"}{reqMark}</span>
                                <DatePicker
                                    picker={form.period === "Yearly" ? "year" : form.period === "Quarterly" ? "quarter" : "month"}
                                    size="large" style={{ width: "100%" }}
                                    format={form.period === "Yearly" ? "YYYY" : form.period === "Quarterly" ? "[Q]Q YYYY" : "MMMM YYYY"}
                                    value={form.target_month} onChange={handleMonthChange} allowClear={false}
                                />
                            </Col>
                            <Col xs={24} md={4}>
                                <span style={labelStyle}>Start Date</span>
                                <DatePicker size="large" style={{ width: "100%" }} format="DD MMM YYYY"
                                    value={form.start_date} onChange={(v) => setField("start_date", v)} />
                            </Col>
                            <Col xs={24} md={5}>
                                <span style={labelStyle}>End Date</span>
                                <DatePicker size="large" style={{ width: "100%" }} format="DD MMM YYYY"
                                    value={form.end_date} onChange={(v) => setField("end_date", v)} />
                            </Col>
                        </Row>
                    </Card>

                    {/* 3. Target Metrics */}
                    <Card title={<CardTitle no="3" icon={<AimOutlined />} text="Target Metrics" />}>
                        <Row gutter={[16, 16]}>
                            {metrics.map((m) => (
                                <Col xs={24} sm={12} md={6} key={m.key}>
                                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                                        <div style={{
                                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                                            background: `${m.color}1a`, color: m.color,
                                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                                        }}>
                                            {m.icon}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <span style={labelStyle}>{m.label}</span>
                                            <Input
                                                type="number" min={0} size="large"
                                                value={form[m.key]}
                                                onChange={(e) => {
                                                    setField(m.key, e.target.value);
                                                    if (form.target_for === "Sales Team") {
                                                        redistributeMetric(m.key, e.target.value);
                                                    }
                                                }}
                                                placeholder={m.money ? "0.00" : "0"}
                                            />

                                            {form.target_for === "Sales Team" && teamMemberIds().length > 0 && (
                                                <div style={{ marginTop: 8, paddingLeft: 4, borderLeft: `2px solid ${m.color}40` }}>
                                                    {teamMemberIds().map((id) => {
                                                        const exec = executives.find((e) => String(e.id) === String(id));
                                                        const locked = (lockedCells[m.key] || new Set()).has(String(id));
                                                        return (
                                                            <div key={id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, paddingLeft: 8 }}>
                                                                <span style={{ fontSize: 12, color: "#666", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {exec?.name || id}
                                                                </span>
                                                                <Input
                                                                    type="number" min={0} max={cellMax(m.key, id)} size="small"
                                                                    status={Number((memberSplits[m.key] || {})[id]) > cellMax(m.key, id) ? "error" : ""}
                                                                    style={{ width: 90, borderColor: locked ? m.color : undefined }}
                                                                    value={(memberSplits[m.key] || {})[id] ?? 0}
                                                                    onChange={(e) => handleMemberCellChange(m.key, id, e.target.value)}
                                                                    onBlur={() => handleMemberCellBlur(m.key, id)}
                                                                    placeholder={m.money ? "0.00" : "0"}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                    {(() => {
                                                        const total = Number(form[m.key]) || 0;
                                                        const assigned = teamMemberIds()
                                                            .reduce((s, id) => s + (Number((memberSplits[m.key] || {})[id]) || 0), 0);
                                                        const over = assigned > total;
                                                        return (
                                                            <div style={{ fontSize: 11, marginTop: 2, paddingLeft: 8, color: over ? "#cf1322" : "#999" }}>
                                                                Assigned {assigned} / {total}
                                                                {over ? " (over limit)" : assigned < total ? ` (${total - assigned} left)` : " ✓"}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>

                    <Row gutter={16}>
                        {/* Additional Information */}
                        <Col xs={24} lg={12} style={{ display: "flex" }}>
                            <Card style={{ width: "100%" }}
                                title={<CardTitle icon={<InfoCircleOutlined />} text="Additional Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={8}>
                                        <span style={labelStyle}>Priority</span>
                                        <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                            options={priorityOptions} value={form.priority}
                                            onChange={(v) => setField("priority", v)}
                                            filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                    </Col>
                                    <Col xs={24} md={16}>
                                        <span style={labelStyle}>Remarks</span>
                                        <Input.TextArea rows={3} maxLength={250} showCount
                                            placeholder="Enter remarks (optional)"
                                            value={form.remarks} onChange={(e) => setField("remarks", e.target.value)} />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        {/* Notification & Reminders */}
                        <Col xs={24} lg={12} style={{ display: "flex" }}>
                            <Card style={{ width: "100%" }}
                                title={<CardTitle icon={<BellOutlined />} text="Notification & Reminders" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <span style={labelStyle}>Send Notification To{reqMark}</span>
                                        <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                            options={notifyOptions} value={form.notify_to}
                                            onChange={(v) => setField("notify_to", v)}
                                            filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <span style={labelStyle}>Reminder</span>
                                        <Select showSearch allowClear size="large" style={{ width: "100%" }}
                                            options={reminderOptions} value={form.reminder}
                                            onChange={(v) => setField("reminder", v)}
                                            filterOption={(i, o) => (o?.label ?? "").toLowerCase().includes(i.toLowerCase())} />
                                    </Col>
                                    <Col xs={24} style={{ marginTop: 12 }}>
                                        <Checkbox checked={form.send_email}
                                            onChange={(e) => setField("send_email", e.target.checked)}>
                                            Send Email Notification
                                        </Checkbox>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>

                    <div style={{ textAlign: "right", marginTop: 8 }}>
                        <Button onClick={() => navigate(-1)} size="large"
                            style={{ marginRight: 8, color: PRIMARY, borderColor: PRIMARY }}>
                            Cancel
                        </Button>
                        <Button size="large" style={{ marginRight: 8 }} onClick={handleReset}>Reset</Button>
                        <Tooltip title={!perm.add ? "You don't have permission to create" : ""}>
                            <Button type="primary" size="large" loading={saving} onClick={handleSave}
                                disabled={isView || (editId ? !perm.edit : !perm.add)}
                                style={{ background: PRIMARY, borderColor: PRIMARY }}>
                                {editId ? "Update Target" : "Save Target"}
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </Card>
        </>
    );
};

export default AddSalesTarget;